import { __rest, __assign } from 'tslib';
import * as React from 'react';
import classNames from 'classnames';
import { AnimationContext } from '../../hooks/animation/animationContext.esm.js';
import { getClassname, fadeInAnimationClassname } from '../../hooks/animation/classManipulation.esm.js';
import useTriggerAnimationInit from '../../hooks/animation/useTriggerAnimationInit.esm.js';
import { ImageAnimationType } from '../../types/animation.esm.js';
import { useImageConfig } from './useImageConfig.esm.js';
import styles from './DefaultImage.module.esm.js';

var defaultAnimation = fadeInAnimationClassname;
var getImageAnimationType = function (imageAnimation) {
    switch (imageAnimation) {
        case 'fadeIn':
            return ImageAnimationType.FadeIn;
        case 'fadeDown':
            return ImageAnimationType.FadeDown;
        case 'fadeInLeft':
            return ImageAnimationType.FadeInLeft;
        case 'fadeInRight':
            return ImageAnimationType.FadeInRight;
        case 'fadeUp':
            return ImageAnimationType.FadeUp;
        default:
            return ImageAnimationType.None;
    }
};
var animationClassName = function (_a) {
    var imageAnimation = _a.imageAnimation;
    switch (imageAnimation) {
        case 'none': {
            return undefined;
        }
        case undefined: {
            return defaultAnimation;
        }
        default:
            return getClassname(getImageAnimationType(imageAnimation));
    }
};
var contentTypeAllowedToConvert = function (contentType) {
    var formatsToConvert = ['image/png', 'image/jpg', 'image/jpeg'];
    return formatsToConvert.includes(contentType);
};
var buildImageUrl = function (imageConfig, url, maxImageWidth) {
    return function (size, shouldBeConverted) {
        var defaultImageOptions = {
            fit: 'pad',
            q: 66,
        };
        if (size) {
            var currentImageSize = imageConfig[size];
            defaultImageOptions.w = !!maxImageWidth && currentImageSize > maxImageWidth ? maxImageWidth : currentImageSize;
        }
        if (shouldBeConverted) {
            defaultImageOptions.fm = 'webp';
        }
        var optionsConverted = Object.entries(defaultImageOptions)
            .map(function (v) { return v.join('='); })
            .join('&');
        return "".concat(url, "?").concat(optionsConverted);
    };
};
var DefaultImage = function (_a) {
    var asset = _a.asset, alt = _a.alt, role = _a.role, _b = _a.classes, parentClasses = _b === void 0 ? {} : _b, onLoad = _a.onLoad, imageAnimation = _a.imageAnimation, maxImageWidth = _a.maxImageWidth, props = __rest(_a, ["asset", "alt", "role", "classes", "onLoad", "imageAnimation", "maxImageWidth"]);
    var elementRef = useTriggerAnimationInit(AnimationContext).elementRef;
    var imageConfig = useImageConfig();
    var _c = (asset === null || asset === void 0 ? void 0 : asset.fields.file) || { contentType: '', url: '' }, url = _c.url, contentType = _c.contentType;
    var generateImageUrl = buildImageUrl(imageConfig, url, maxImageWidth);
    var shouldBeConverted = React.useMemo(function () { return contentTypeAllowedToConvert(contentType); }, [contentType]);
    var generateSrc = function (width) {
        if (width === void 0) { width = 3060; }
        return "".concat(generateImageUrl(undefined, shouldBeConverted), "&w=").concat(width);
    };
    if (!asset) {
        return null;
    }
    return (React.createElement("picture", { className: classNames(parentClasses.picture, styles.componentStyles) },
        shouldBeConverted && (React.createElement("source", { media: "(min-width: 1260px)", srcSet: generateImageUrl('extraLargeImageWidth', true), type: "image/webp" })),
        React.createElement("source", { media: "(min-width: 1260px)", srcSet: generateImageUrl('extraLargeImageWidth'), type: contentType }),
        shouldBeConverted && (React.createElement("source", { media: "(min-width: 1020px)", srcSet: generateImageUrl('largeImageWidth', true), type: "image/webp" })),
        React.createElement("source", { media: "(min-width: 1020px)", srcSet: generateImageUrl('largeImageWidth'), type: contentType }),
        shouldBeConverted && (React.createElement("source", { media: "(min-width: 720px)", srcSet: generateImageUrl('mediumImageWidth', true), type: "image/webp" })),
        React.createElement("source", { media: "(min-width: 720px)", srcSet: generateImageUrl('mediumImageWidth'), type: contentType }),
        shouldBeConverted && (React.createElement("source", { media: "(min-width: 375px)", srcSet: generateImageUrl('smallImageWidth', true), type: "image/webp" })),
        React.createElement("source", { media: "(min-width: 375px)", srcSet: generateImageUrl('smallImageWidth'), type: contentType }),
        shouldBeConverted && (React.createElement("source", { media: "(min-width: 360px)", srcSet: generateImageUrl('smallReducedGutterImageWidth', true), type: "image/webp" })),
        React.createElement("source", { media: "(min-width: 360px)", srcSet: generateImageUrl('smallReducedGutterImageWidth'), type: contentType }),
        shouldBeConverted && (React.createElement("source", { media: "(min-width: 320px)", srcSet: generateImageUrl('extraSmallImageWidth', true), type: "image/webp" })),
        React.createElement("source", { media: "(min-width: 320px)", srcSet: generateImageUrl('extraSmallImageWidth'), type: contentType }),
        React.createElement("img", __assign({ alt: alt, className: animationClassName({ imageAnimation: imageAnimation }), loading: "lazy", onLoad: onLoad, ref: elementRef, role: role, src: generateSrc(maxImageWidth) }, props))));
};

export { DefaultImage, buildImageUrl, contentTypeAllowedToConvert };
//# sourceMappingURL=DefaultImage.esm.js.map
